/**
 * Utility functions for secure asset management with Storyblok
 */

export interface SecureAssetInfo {
  id: string
  filename?: string
  size?: number
  contentType?: string
  available: boolean
}

/**
 * Extract asset ID from Storyblok asset URL
 * @param assetUrl - Full Storyblok asset URL
 * @returns Asset ID or null if invalid URL
 */
export function extractAssetIdFromUrl(assetUrl: string): string | null {
  try {
    // Storyblok asset URLs typically follow pattern: https://a.storyblok.com/f/{space_id}/{asset_id}/{filename}
    const urlPattern = /\/f\/\d+\/([^/]+)\//
    const match = assetUrl.match(urlPattern)
    return match ? match[1] : null
  } catch {
    return null
  }
}

/**
 * Check if an asset is available for download
 * @param assetId - Storyblok asset ID
 * @returns Promise with asset availability info
 */
export async function checkAssetAvailability(assetId: string): Promise<SecureAssetInfo> {
  try {
    const response = await fetch(`/api/secure-download?assetId=${encodeURIComponent(assetId)}`)

    if (!response.ok) {
      return { id: assetId, available: false }
    }

    const data = await response.json()
    return {
      id: assetId,
      filename: data.filename,
      size: data.size,
      contentType: data.contentType,
      available: data.available,
    }
  } catch (error) {
    console.error('Error checking asset availability:', error)
    return { id: assetId, available: false }
  }
}

/**
 * Request secure download for an asset
 * @param assetId - Storyblok asset ID
 * @param email - User email address
 * @param accessCode - Optional access code
 * @returns Promise with download URL or error
 */
export async function requestSecureDownload(
  assetId: string,
  email: string,
  accessCode?: string
): Promise<{ success: boolean; downloadUrl?: string; error?: string; expiresAt?: string }> {
  try {
    const response = await fetch('/api/secure-download', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        assetId,
        email,
        accessCode,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error:
          typeof data.error === 'string' && data.error.length > 0
            ? data.error
            : 'Failed to generate download link',
      }
    }

    return {
      success: true,
      downloadUrl: data.downloadUrl,
      expiresAt: data.expiresAt,
    }
  } catch (error) {
    console.error('Error requesting secure download:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error occurred',
    }
  }
}

/**
 * Validate email format
 * @param email - Email address to validate
 * @returns Boolean indicating if email is valid
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Format file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  const sizeUnit = i >= 0 && i < sizes.length ? (sizes.at(i) ?? 'Unknown') : 'Unknown'
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizeUnit
}

/**
 * Get file extension from content type
 * @param contentType - MIME type
 * @returns File extension
 */
export function getFileExtensionFromContentType(contentType: string): string {
  const mimeToExt: Record<string, string> = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'application/zip': 'zip',
    'text/plain': 'txt',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  }

  // Use Map for safer property access
  const extensionMap = new Map(Object.entries(mimeToExt))
  const extension = extensionMap.get(contentType)
  return typeof extension === 'string' ? extension : 'file'
}
